import { useEffect } from 'react';

/**
 * Global keyboard capture: refocus scanner input when typing starts.
 * USB scanners type characters rapidly as keyboard input — this ensures
 * keystrokes reach the `[data-scanner-input]` field even when focus has
 * drifted to a button or the page body.
 *
 * @param opts.paused - Disables capture when modals/dialogs are open
 */
export function useScannerAutoFocus(opts?: { paused?: boolean }) {
  const paused = opts?.paused ?? false;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (paused) return;

      // Don't capture modifier combos
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      // Don't capture function keys (F1-F12)
      if (/^F([1-9]|1[0-2])$/.test(e.key)) return;

      // Don't capture navigation, activation, or non-printable keys.
      // Space/Enter must be excluded to preserve button activation via keyboard
      // (Space fires on keyup — moving focus on keydown would break it).
      // Arrow keys are used for tab lists, radio groups, and scrolling.
      if (
        [
          'Tab', 'Escape', 'Shift', 'Control', 'Alt', 'Meta',
          'Enter', ' ',
          'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
          'Home', 'End', 'PageUp', 'PageDown',
        ].includes(e.key)
      ) return;

      // Check if already focused on the scanner input
      const allInputs = document.querySelectorAll('[data-scanner-input]');
      const scannerInput = allInputs[allInputs.length - 1] as HTMLInputElement | undefined;
      if (!scannerInput || document.activeElement === scannerInput) return;

      // Don't steal focus from other inputs or editable elements
      const active = document.activeElement;
      const activeTag = active?.tagName.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select') return;
      if (active instanceof HTMLElement && active.isContentEditable) return;

      // Focus the scanner and let the character through
      scannerInput.focus({ preventScroll: true });
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [paused]);
}
